/**
 * Task 15 — strip server-owned and local-only fields before any Supabase write (docs/08 §6, §8c).
 * Never send created_at / updated_at; never send Dexie sync bookkeeping.
 */

const SERVER_OWNED = new Set(['created_at', 'updated_at']);
const LOCAL_ONLY = new Set(['_localDirty', '_baseServerUpdatedAt']);

function wireValue(value: unknown): unknown {
  if (value === null || typeof value === 'boolean' || typeof value === 'string') {
    return value;
  }
  if (typeof value === 'bigint') {
    if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER)) {
      throw new Error('stripServerFields: bigint outside safe integer range');
    }
    return Number(value);
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || !Number.isInteger(value)) {
      throw new Error('stripServerFields: non-integer number');
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => wireValue(item));
  }
  if (typeof value === 'object') {
    return stripServerFields(value as Record<string, unknown>);
  }
  throw new Error('stripServerFields: unsupported type');
}

/**
 * Shared outbound scrubber (docs/08 §6). Runs on every remote insert/update payload.
 * Returns a JSON-safe object (bigint → number). Does not mutate the input.
 */
export function stripServerFields(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (SERVER_OWNED.has(key) || LOCAL_ONLY.has(key)) continue;
    out[key] = wireValue(value);
  }
  return out;
}
